import { call, put, takeLatest } from "redux-saga/effects";
import { resolveLatLongForServiceability } from "../helpers/savedAddressLatLong";
import {
  fetchBlockedDatesDetails,
  fetchEarliestTimeSlot,
  fetchGetDeliveryDateAndGeoPriceDetails,
  fetchInternationalDeliveryDates,
  fetchInternationalShippingAndTimeSlot,
  fetchPriceAndEarliestShipDetails,
  fetchCheckoutShippingAndTimeSlotDetails,
  fetchShippingAndTimeSlotDetails,
} from "../../../api/common/shippingDateAndSlotSelection";
import {
  requestToFetchDeliveryDatesAction,
  successToFetchDeliveryDatesAction,
  failToFetchDeliveryDatesAction,
  successToFetchBlockedDatesAction,
  requestToFetchShippingAndTimeSlotAction,
  successToFetchShippingAndTimeSlotAction,
  failToFetchShippingAndTimeSlotAction,
  requestToFetchGetDeliveryDateAndGeoPriceAction,
  successToFetchGetDeliveryDateAndGeoPriceAction,
  failToFetchGetDeliveryDateAndGeoPriceAction,
  requestToFetchBlockedDatesAction,
  requestToFetchInternationalShippingMethod,
  responseFromInternationalShippingMethod,
  responseFromInternationalDeliveryDates,
  requestToFetchInternationalDeliveryDates,
  requestToFetchInternationalShippingAndTimeSlot,
  successToFetchInternationalShippingAndTimeSlot,
  failToFetchInternationalShippingAndTimeSlot,
  clearOldInternationalShippingMethod,
  clearDeliveryDetailsAction,
  requestToFetchEarliestTimeSlot,
  successToFetchEarliestTimeSlotDetailsAction,
  failToFetchEarliestTimeSlotDetailsAction,
  successToFetchCombinedCheckoutShippingAction,
} from "../../slices/common/shippingDateAndSlotSelection";
import { convertShippingResponse } from "../../../utils/common/timeSlot/shippingMethodsHelper";
import { normalizeCheckoutShippingDetailsResponse } from "../../../utils/checkout/deliveryDateAndTimeSlot";

/**
 *
 * @param {*} action
 */
function* getPriceAndEarliestShipDetailsSaga({ payload }) {
  try {
    const latLong = yield call(resolveLatLongForServiceability);
    // Cart item's selected address (checkout): keep its own lat / long / accuracyType.
    const requestPayload = payload?.isSelectedAddressLocation ? payload : { ...payload, ...latLong };

    const response = yield fetchPriceAndEarliestShipDetails(requestPayload);
    if (response?.status === 500) {
      yield put(failToFetchDeliveryDatesAction());
    } else {
      yield put(clearDeliveryDetailsAction());
      yield put(successToFetchDeliveryDatesAction(response?.data));
    }
  } catch (error) {
    yield put(failToFetchDeliveryDatesAction(error));
  }
}

/**
 *
 * @param {*} action
 */
function* getEarliestTimeSlotSaga({ payload }) {
  try {
    const latLong = yield call(resolveLatLongForServiceability);
    const response = yield fetchEarliestTimeSlot({ ...payload, ...latLong });
    yield put(successToFetchEarliestTimeSlotDetailsAction(response?.data?.earliestTimeSlot));
  } catch (error) {
    yield put(failToFetchEarliestTimeSlotDetailsAction(error?.message));
  }
}

/**
 *
 * @param {*} action
 */
function* getBlockedDatesSaga({ payload }) {
  try {
    const blockedDates = yield fetchBlockedDatesDetails(payload);
    if (blockedDates) yield put(successToFetchBlockedDatesAction(blockedDates));
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 * @param {*} action
 */
function* getShippingAndTimeSlotSaga({ payload }) {
  try {
    const { isFromCheckout, isCombinedCheckout, lat, long, ...requestPayload } = payload || {};
    const response = isFromCheckout
      ? yield fetchCheckoutShippingAndTimeSlotDetails({ ...requestPayload, lat: lat || 28, long: long || 77 })
      : yield fetchShippingAndTimeSlotDetails(requestPayload);

    if (isFromCheckout) {
      const normalizedResponse = normalizeCheckoutShippingDetailsResponse(response);

      if (isCombinedCheckout) {
        yield put(successToFetchCombinedCheckoutShippingAction(normalizedResponse));
        return;
      }

      yield put(successToFetchShippingAndTimeSlotAction(normalizedResponse));
      return;
    }

    yield put(successToFetchShippingAndTimeSlotAction(response));
  } catch (error) {
    yield put(failToFetchShippingAndTimeSlotAction(error));
  }
}

/**
 *
 * @param {*} action
 */
function* getInternationalShippingAndTimeSlot({ payload }) {
  try {
    const response = yield fetchShippingAndTimeSlotDetails(payload);
    const convertedShippingResponse = convertShippingResponse(response);
    yield put(successToFetchInternationalShippingAndTimeSlot(convertedShippingResponse?.data));
  } catch (error) {
    yield put(failToFetchInternationalShippingAndTimeSlot(error));
  }
}

/**
 *
 * @param {*} action
 */
function* getDeliveryDateAndGeoPrice({ payload }) {
  try {
    const response = yield fetchGetDeliveryDateAndGeoPriceDetails(payload);

    yield put(successToFetchGetDeliveryDateAndGeoPriceAction(response?.data));
  } catch (error) {
    yield put(failToFetchGetDeliveryDateAndGeoPriceAction(error));
  }
}
/**
 *
 * @param {*} action
 */
function* getInternationalShippingMethod({ payload }) {
  try {
    const response = yield fetchInternationalShippingAndTimeSlot(payload);
    if (response?.responseStatus === "success") {
      yield put(clearOldInternationalShippingMethod());
      const parsedData = JSON.parse(response?.data || {});
      yield put(responseFromInternationalShippingMethod(parsedData));
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 *
 * @param {*} action
 */
function* getInternationalDeliveryDates({ payload }) {
  try {
    const response = yield fetchInternationalDeliveryDates(payload);
    const parsedData = JSON.parse(response?.data || {});
    if (response?.responseStatus === "success") {
      yield put(responseFromInternationalDeliveryDates(parsedData));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Description placeholder
 *
 * @export
 * @returns {{}}
 */
export function* watcherShippingDateAndSlotSelectionSaga() {
  yield takeLatest(requestToFetchDeliveryDatesAction.type, getPriceAndEarliestShipDetailsSaga);
  yield takeLatest(requestToFetchBlockedDatesAction.type, getBlockedDatesSaga);
  yield takeLatest(requestToFetchShippingAndTimeSlotAction.type, getShippingAndTimeSlotSaga);
  yield takeLatest(requestToFetchGetDeliveryDateAndGeoPriceAction.type, getDeliveryDateAndGeoPrice);
  yield takeLatest(requestToFetchInternationalShippingMethod.type, getInternationalShippingMethod);
  yield takeLatest(requestToFetchInternationalDeliveryDates.type, getInternationalDeliveryDates);
  yield takeLatest(requestToFetchInternationalShippingAndTimeSlot.type, getInternationalShippingAndTimeSlot);
  yield takeLatest(requestToFetchEarliestTimeSlot.type, getEarliestTimeSlotSaga);
}
